// src/pages/stockTransfer/StockTransferDetailsPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftRight, ArrowLeft, Printer, Edit2, Trash2, CheckCircle, Loader2, ArrowRight } from 'lucide-react';
import { PageHeader, PrintPreviewModal } from '../../components/common';
import ProductThumb from '../procurement/components/ProductThumb';
import StockTransferA4Document from './StockTransferA4Document';
import { getStockTransferById, completeStockTransfer, deleteStockTransfer } from '../../services/api/stockTransfer';
import { usePermissions } from '../../hooks/usePermissions';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { formatDate, formatDateTime } from '../../utils/dateUtils';
import { getTransferItemDisplayName, getTransferItemSku } from './transferItemUtils';
import styles from './StockTransferDetailsPage.module.css';

const StockTransferDetailsPage = () => {
  const navigate = useNavigate();
  const { transferId } = useParams();
  const { hasPermission } = usePermissions();
  const { t } = useLocalization();
  const { success, error, confirm } = useAlert();

  const [transfer, setTransfer] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPrint, setShowPrint] = useState(false);

  useEffect(() => {
    loadTransfer();
  }, [transferId]);

  const loadTransfer = async () => {
    try {
      setIsLoading(true);
      const response = await getStockTransferById(transferId);
      setTransfer(response.transfer);
    } catch (err) {
      console.error('Failed to load transfer:', err);
      error(err.response?.data?.message || t('stockTransfer.failedToLoad'));
      navigate('/inventory/stock-transfers');
    } finally {
      setIsLoading(false);
    }
  };

  const handleComplete = async () => {
    if (!(await confirm(t('stockTransfer.confirmCompleteMessage')))) return;
    try {
      setIsProcessing(true);
      await completeStockTransfer(transferId);
      success(t('stockTransfer.transferCompleted'));
      await loadTransfer();
    } catch (err) {
      console.error('Failed to complete transfer:', err);
      error(err.response?.data?.message || t('stockTransfer.failedToComplete'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDelete = async () => {
    if (!(await confirm(t('stockTransfer.confirmDelete')))) return;
    try {
      setIsProcessing(true);
      await deleteStockTransfer(transferId);
      success(t('stockTransfer.transferDeleted'));
      navigate('/inventory/stock-transfers');
    } catch (err) {
      console.error('Failed to delete transfer:', err);
      error(err.response?.data?.message || t('stockTransfer.failedToDelete'));
      setIsProcessing(false);
    }
  };

  if (isLoading || !transfer) {
    return (
      <div className={styles.loadingState}>
        <Loader2 className={styles.spinning} size={48} />
      </div>
    );
  }

  const isDraft = transfer.status === 'draft';
  const items = transfer.items || [];

  const renderWarehouse = (label, warehouse) => (
    <div className={styles.warehouseBox}>
      <div className={styles.boxLabel}>{label}</div>
      <div className={styles.warehouseName}>{warehouse?.name || '-'}</div>
      {warehouse?.address && <div className={styles.warehouseMeta}>{warehouse.address}</div>}
      {warehouse?.phone && <div className={styles.warehouseMeta}>{warehouse.phone}</div>}
    </div>
  );

  return (
    <div className={styles.detailsPage}>
      <PageHeader
        title={transfer.transferNumber}
        description={t('stockTransfer.detailsDescription')}
        icon={ArrowLeftRight}
        actions={
          <div className={styles.headerActions}>
            <button onClick={() => navigate('/inventory/stock-transfers')} className={styles.secondaryButton}>
              <ArrowLeft size={18} />
              {t('stockTransfer.backToTransfers')}
            </button>
            <button onClick={() => setShowPrint(true)} className={styles.secondaryButton}>
              <Printer size={18} />
              {t('stockTransfer.printNote')}
            </button>
            {isDraft && hasPermission('create_stock_transfer') && (
              <>
                <button onClick={() => navigate(`/inventory/stock-transfers/${transferId}/edit`)} className={styles.secondaryButton} disabled={isProcessing}>
                  <Edit2 size={18} />
                  {t('common.edit')}
                </button>
                <button onClick={handleDelete} className={styles.dangerButton} disabled={isProcessing}>
                  <Trash2 size={18} />
                  {t('common.delete')}
                </button>
              </>
            )}
            {isDraft && hasPermission('complete_stock_transfer') && (
              <button onClick={handleComplete} className={styles.primaryButton} disabled={isProcessing}>
                {isProcessing ? <Loader2 className={styles.spinning} size={18} /> : <CheckCircle size={18} />}
                {t('stockTransfer.complete')}
              </button>
            )}
          </div>
        }
      />

      <div className={styles.noteCard}>
        <div className={styles.noteHeader}>
          <div>
            <div className={styles.boxLabel}>{t('stockTransfer.transferNumber')}</div>
            <div className={styles.transferNumber}>{transfer.transferNumber}</div>
          </div>
          <div className={styles.noteMeta}>
            <div><span>{t('stockTransfer.transferDate')}:</span> <strong>{formatDate(transfer.transferDate)}</strong></div>
            <div>
              <span>{t('stockTransfer.status')}:</span>{' '}
              <span className={`${styles.statusBadge} ${isDraft ? styles.statusDraft : styles.statusCompleted}`}>
                {isDraft ? t('stockTransfer.statusDraft') : t('stockTransfer.statusCompleted')}
              </span>
            </div>
          </div>
        </div>

        <div className={styles.warehouseRow}>
          {renderWarehouse(t('stockTransfer.fromWarehouse'), transfer.fromWarehouse)}
          <ArrowRight className={styles.arrowIcon} size={28} />
          {renderWarehouse(t('stockTransfer.toWarehouse'), transfer.toWarehouse)}
        </div>

        <div className={styles.tableWrapper}>
          <table className={styles.itemsTable}>
            <thead>
              <tr>
                <th className={styles.indexCol}>#</th>
                <th>{t('stockTransfer.product')}</th>
                <th className={styles.alignRight}>{t('stockTransfer.quantity')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={item.id}>
                  <td className={styles.indexCol}>{index + 1}</td>
                  <td>
                    <div className={styles.productCell}>
                      <ProductThumb item={item} alt={getTransferItemDisplayName(item)} />
                      <div>
                        <div className={styles.productName}>{getTransferItemDisplayName(item) || t('common.na')}</div>
                        <div className={styles.productSku}>{t('stockTransfer.sku')}: {getTransferItemSku(item) || '-'}</div>
                      </div>
                    </div>
                  </td>
                  <td className={`${styles.alignRight} ${styles.qtyCell}`}>{parseFloat(item.quantity)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td></td>
                <td>{t('stockTransfer.totalItems')}: {items.length}</td>
                <td className={styles.alignRight}>{parseFloat(transfer.totalQuantity || 0)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {transfer.notes && (
          <div className={styles.notesBox}>
            <div className={styles.boxLabel}>{t('stockTransfer.notes')}</div>
            <p>{transfer.notes}</p>
          </div>
        )}

        <div className={styles.auditRow}>
          <span>{t('stockTransfer.createdBy')}: <strong>{transfer.createdBy?.name || '-'}</strong></span>
          {transfer.completedBy && (
            <>
              <span>{t('stockTransfer.completedBy')}: <strong>{transfer.completedBy.name}</strong></span>
              <span>{t('stockTransfer.completedAt')}: <strong>{formatDateTime(transfer.completedAt)}</strong></span>
            </>
          )}
        </div>
      </div>

      {showPrint && (
        <PrintPreviewModal
          title={`${t('stockTransfer.stockTransferNote')} — ${transfer.transferNumber}`}
          onClose={() => setShowPrint(false)}
          pdfFileName={`Stock_Transfer_Note_${transfer.transferNumber}.pdf`}
          renderA4={() => <StockTransferA4Document transfer={transfer} />}
        />
      )}
    </div>
  );
};

export default StockTransferDetailsPage;
